import { ExtensionTokenBridge } from '@/features/extension/ExtensionTokenBridge'
import { ExtensionPrompt } from '@/features/extension/ExtensionPrompt'
import { useExtensionStatus, versionAtLeast } from '@/features/extension/useExtensionStatus'
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router'
import {
  LayoutDashboard,
  Handshake,
  Lightbulb,
  FileText,
  MessageSquareWarning,
  Map as MapIcon,
  Gavel,
  Archive,
  Briefcase,
  ChartNoAxesCombined,
  Headset,
  SquareActivity,
  UsersRound,
  Building,
  Building2,
  ChartColumn,
  SquareKanban,
  Rocket,
  CheckCheck,
  ChevronDown,
  Download,
  FolderKanban,
  FolderOpen,
  FilePenLine,
  Inbox,
  ImagePlus,
  LifeBuoy,
  LogOut,
  Menu,
  MessageSquarePlus,
  Settings,
  Users,
  WalletCards,
  CalendarDays,
  MessagesSquare,
  ClipboardList,
  Globe2,
  MonitorDown,
  Sparkles,
  AlarmClock,
  Network,
  Target,
  CircleDollarSign,
  Contact,
  Mail,
  ListChecks,
  Gauge,
  BookOpenCheck,
  TrendingUp,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  PanelLeft,
  ShoppingCart, Settings2, Eye, Workflow, BriefcaseBusiness, BookOpen, NotebookPen, Sun, PhoneOutgoing, CalendarClock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SidebarNav, flattenNavGroups, type NavGroup } from '@/components/layout/sidebar-nav'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { UserAvatar } from '@/components/composites/user-avatar'
import { useAuth } from '@/features/auth/AuthProvider'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { ProfilePhotoDialog } from '@/features/settings/ProfilePhotoDialog'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { MusicButton } from '@/features/music'
import { GlobalCommandPalette } from '@/components/layout/global-command-palette'
import { BrandMark } from '@/components/composites/brand-mark'
import { toast } from 'sonner'
import { TimeZoneDialog, useTimeZonePreference } from '@/features/preferences'
import { shortTimeZoneLabel } from '@/lib/timezone'
import { useInstallApp } from '@/lib/pwa'
import { DynamicIsland, IslandSettingsDialog } from '@/features/island'
import { FocusModeDialog, untilLabel, useFocusMode } from '@/features/focus'
import { cn } from '@/lib/utils'
import { useSidebarCollapsed } from './useSidebarCollapsed'
import { StageRequestHost } from '@/features/cards/StageRequests'
import { StagePromptHost } from '@/features/cards/StagePrompts'
import { SidebarDock } from './sidebar-dock'
import { SidebarSettingsDialog, usePreferences, useSidebarBehavior } from '@/features/preferences'
import { ViewAsBanner, ViewAsDialog, getViewAs } from '@/features/view-as'
import { GoogleCalendarDialog } from '@/features/calendar'
import { useCommsSummary } from '@/features/comms'
import { useDeskBadge, useLegacySupportFrozen } from '@/features/support-desk'
import { useProjectsNav } from '@/features/client-projects'
import { usePipelineSettings } from '@/features/pipeline'
import { useCrm2Settings } from '@/features/crm2'
import { ActiveWorkChip, StickyWorkBar } from '@/features/worktime'
import { GamesHost, OfficeWidget } from '@/features/office'
import { DiaryGate } from '@/features/diary'
import { usePermission } from '@/features/permissions/usePermission'
import { useWorkspace } from '@/features/workspace/WorkspaceProvider'
import { paths } from '@/routes/paths'
import { DRAFTS_CHANGED_EVENT, listLocalDrafts } from '@/features/drafts/draftStore'

/**
 * The app frame: 232px sidebar, 60px topbar — both straight from the SERA layout tokens.
 *
 * Nav entries are HIDDEN when the user cannot read the thing they point at (as opposed to
 * disabled, which is for actions). See the conventions in usePermission.ts.
 *
 * The nav is split into two groups. "Delivery" is the daily loop — what is being worked on,
 * for whom. "Organization" is the structure behind it, which is consulted rather than lived
 * in. One flat list of six made every destination look equally likely; they are not.
 */
const CRM_TABS = new Set(['accounts', 'overview', 'leads', 'deals', 'forecast', 'contacts', 'conversations', 'followups', 'performance', 'playbook'])

export function AppShell() {
  const extension = useExtensionStatus()
  const { workspaceSlug = '' } = useParams()
  const { can, has } = usePermission()
  const { workspace, workspaceId } = useWorkspace()
  const { status, email, user, signOut } = useAuth()

  /*
   * The membership row, not the token, decides what face is shown.
   *
   * Firebase carries whatever photo the sign-in provider had, which is right up until
   * somebody picks one of their own — after that the token's copy is stale, and it is the
   * membership photo that every colleague sees. Showing them different things is how you get
   * "I changed it and nothing happened".
   */
  /*
   * Owned here, not by the menu item that sets it.
   *
   * The dialog used to live inside the dropdown, which unmounted the whole subtree the
   * moment the menu closed — including an upload still in flight and whatever it had to
   * say about it. Hoisting the state lets the menu close, as a menu should, while the
   * dialog stays.
   */
  const [photoOpen, setPhotoOpen] = useState(false)
  const [timeZoneOpen, setTimeZoneOpen] = useState(false)
  const timeZone = useTimeZonePreference()
  const app = useInstallApp()
  const { pathname } = useLocation()
  const [islandSettingsOpen, setIslandSettingsOpen] = useState(false)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [viewAsOpen, setViewAsOpen] = useState(false)
  const commsSummary = useCommsSummary(getViewAs() === null)
  const deskBadge = useDeskBadge(getViewAs() === null && can('read', 'Card'))
  // Once the older Support pages are frozen they leave Operations and Reports for one Archive folder.
  const legacyFrozen = useLegacySupportFrozen(can('read', 'Card'))
  const projectsNav = useProjectsNav(can('read', 'Project'))
  // The demo-to-contract pipeline: on in staging, off in production until switched on there.
  const pipelineOn = Boolean(usePipelineSettings(can('read', 'Crm') || can('read', 'Project')).data?.on)
  // TapCRMv2 and TapPM (staging preview): their own sections, so nothing overlaps the older CRM and PM pages.
  const crm2On = Boolean(useCrm2Settings(can('read', 'Crm') || can('read', 'Project')).data?.on)
  const viewingAs = getViewAs() !== null
  const [focusOpen, setFocusOpen] = useState(false)
  const sidebarBehavior = useSidebarBehavior()
  const sidebarMagnification = usePreferences().data?.sidebar?.magnification
  const sidebar = useSidebarCollapsed(sidebarBehavior)
  const [sidebarSettingsOpen, setSidebarSettingsOpen] = useState(false)
  const sidebarCollapsed = !sidebar.open
  const shortcut = typeof navigator !== 'undefined' && navigator.platform.includes('Mac') ? '⌘\\' : 'Ctrl+\\'
  const { focus, active: focusOn } = useFocusMode()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [draftEpoch, setDraftEpoch] = useState(0)
  const me = useIdentity()

  useEffect(() => {
    const refreshDraftNav = () => setDraftEpoch((value) => value + 1)
    window.addEventListener(DRAFTS_CHANGED_EVENT, refreshDraftNav)
    window.addEventListener('storage', refreshDraftNav)
    return () => {
      window.removeEventListener(DRAFTS_CHANGED_EVENT, refreshDraftNav)
      window.removeEventListener('storage', refreshDraftNav)
    }
  }, [])

  void draftEpoch
  const hasDrafts = Boolean(me.userId && workspaceId && listLocalDrafts(me.userId, workspaceId).length > 0)
  const meAsPerson = {
    id: me.userId || user?.uid || 'me',
    displayName: me.displayName || user?.displayName || email || 'You',
    photoUrl: me.photoUrl ?? user?.photoURL ?? null,
  }

  // Typed before filtering: only some entries are folders, and the filter below reads `children` on every one.
  const allGroups: NavGroup[] = [
    {
      label: 'Workspace',
      drawer: { icon: LayoutDashboard, blurb: 'Overview, work, calendar and communications' },
      items: [
        { to: paths.overview(workspaceSlug), label: 'Overview', icon: LayoutDashboard, show: can('read', 'Card') },
        { to: paths.work(workspaceSlug), label: 'Work', icon: Briefcase, show: can('read', 'Card') },
        { to: paths.drafts(workspaceSlug), label: 'Drafts', icon: FilePenLine, show: hasDrafts && (can('create', 'Card') || can('create', 'Project') || can('create', 'Client')) },
        // Grain recordings you were in; the same read right as the work they turn into.
        { to: paths.meetings(workspaceSlug), label: 'Calendar', icon: CalendarDays, show: can('read', 'Card') },
        // Everyone has an inbox: it holds only what was addressed to them.
        { to: paths.comms(workspaceSlug), label: 'Communications', icon: MessagesSquare, show: true, badge: commsSummary.data?.open ?? 0 },
        // Who is on what right now, and the office floor. Open to everyone: it is the team's day.
        { to: paths.teamToday(workspaceSlug), label: 'Team today', icon: Users, show: true },
        // Everyone's daily diary: what each person set out to do, and how it went.
        { to: paths.diary(workspaceSlug), label: 'Diary', icon: BookOpen, show: true },
        // Notes, documents, sheets, diagrams and recordings; private by default, shareable with everyone.
        { to: paths.scribble(workspaceSlug), label: 'Scribble Area', icon: NotebookPen, show: true },
        { to: paths.office(workspaceSlug), label: 'Office', icon: Building, show: true },
      ],
    },
    {
      label: 'Operations',
      drawer: { icon: Workflow, blurb: 'Overdue, approvals, requests, support and the archive' },
      items: [
        // Gated on Card for the same reason Insights is: the inbox is a filtered card query,
        // and anyone who can read the work can read which of it is waiting on them.
        { to: paths.overdue(workspaceSlug), label: 'Overdue', icon: AlarmClock, show: can('read', 'Card') },
        { to: paths.approvals(workspaceSlug), label: 'Approvals', icon: CheckCheck, show: can('read', 'Card') },
        // Triage is where an ask becomes a work item, so the entry follows the ability to
        // create one — a reader who cannot accept anything has nothing to do on that screen.
        { to: paths.requests(workspaceSlug), label: 'Requests', icon: Inbox, show: can('create', 'Card') },
        { to: paths.supportTicketQueue(workspaceSlug), label: 'Support', icon: LifeBuoy, show: can('read', 'Card') && !legacyFrozen },
        // Same gate as the board it is the far end of.
        { to: paths.archive(workspaceSlug), label: 'Archive', icon: Archive, show: can('read', 'Card') },
      ],
    },
    {
      // The Support tab: one clock per customer issue. Beside Operations, which is untouched
      // until it is retired.
      label: 'Support',
      drawer: { icon: Headset, blurb: 'Support queue, triage and the live SLA dashboard' },
      items: [
        { to: paths.supportDesk(workspaceSlug), label: 'Queue', icon: Headset, show: can('read', 'Card'), badge: deskBadge.data ?? 0, end: true },
        { to: paths.supportDeskDashboard(workspaceSlug), label: 'Dashboard', icon: Gauge, show: can('read', 'Card') },
      ],
    },
    {
      // TapCRMv2: the CRM in Tapwork, off GoHighLevel. Its own section so the old one can retire whole.
      label: 'TapCRMv2',
      drawer: { icon: Handshake, blurb: 'Today, the five pipelines and accounts, all in Tapwork (staging preview)' },
      items: [
        { to: paths.crm2(workspaceSlug), label: 'Today', icon: Sun, show: crm2On && can('read', 'Crm'), end: true },
        { to: paths.crm2Dashboard(workspaceSlug), label: 'Dashboard', icon: Gauge, show: crm2On && can('read', 'Crm') },
        { to: paths.crm2Board(workspaceSlug), label: 'Pipelines', icon: SquareKanban, show: crm2On && can('read', 'Crm') },
        { to: paths.crm2Accounts(workspaceSlug), label: 'Accounts', icon: Building2, show: crm2On && can('read', 'Crm') },
        { to: paths.crm2Contacts(workspaceSlug), label: 'Contacts', icon: Contact, show: crm2On && can('read', 'Crm') },
        { to: paths.crm2Conversations(workspaceSlug), label: 'Conversations', icon: MessagesSquare, show: crm2On && can('read', 'Crm') },
        { to: paths.crm2Outreach(workspaceSlug), label: 'Outreach', icon: PhoneOutgoing, show: crm2On && can('read', 'Crm') },
        { to: paths.crm2Calendar(workspaceSlug), label: 'Calendar', icon: CalendarClock, show: crm2On && can('read', 'Crm') },
        { to: paths.crm2Settings(workspaceSlug), label: 'Settings', icon: Settings2, show: crm2On && can('manage', 'Crm') },
      ],
    },
    {
      // TapPM: the projects TapCRMv2 opens at Pre-Pilot. Its own section beside Project Management.
      label: 'TapPM',
      drawer: { icon: FolderKanban, blurb: 'Client projects from Discovery to Expansion, and your tasks (staging preview)' },
      items: [
        { to: paths.pm2(workspaceSlug), label: 'Projects', icon: FolderKanban, show: crm2On && can('read', 'Project'), end: true },
        { to: paths.pm2MyTasks(workspaceSlug), label: 'My tasks', icon: ListChecks, show: crm2On && can('read', 'Project') },
      ],
    },
    {
      // The CRM's sections live in a drawer rather than a tab bar on the page or ten rows in the
      // sidebar: one entry opens a panel listing every section.
      label: 'TapCRM',
      drawer: { icon: Handshake, blurb: 'Dashboard, conversations, contacts, accounts, opportunities, tasks and meetings' },
      items: [
        // Working screens over GoHighLevel, the system of record, in GHL's own menu order, listed
        // straight under TapCRM. Dashboard first, so opening TapCRM lands on it.
        // The pipeline (staging preview): one board from demo to contract, its check-in and report.
        { to: paths.pipeline(workspaceSlug), label: 'Pipeline', icon: Workflow, show: pipelineOn && (can('read', 'Crm') || can('read', 'Project')), end: true, matchSearch: (q: URLSearchParams) => q.get('view') !== 'pilots' },
        { to: paths.pipelineCheckin(workspaceSlug), label: 'Weekly check-in', icon: CheckCheck, show: pipelineOn && can('read', 'Crm') },
        ...([
          [paths.crmDashboard(workspaceSlug), 'Dashboard', ChartColumn],
          [paths.crmConversations(workspaceSlug), 'Conversations', MessagesSquare],
          [paths.crmContacts(workspaceSlug), 'Contacts', Contact],
          [paths.crmAccounts(workspaceSlug), 'Accounts', Building2],
          [paths.crmBoard(workspaceSlug), 'Opportunities', SquareKanban],
          [paths.crmTasks(workspaceSlug), 'Tasks', ListChecks],
          [paths.crmMeetings(workspaceSlug), 'Meetings', CalendarDays],
        ] as const).map(([to, label, icon]) => ({ to, label, icon, show: can('read', 'Crm') })),
        ...([
        ['accounts', 'Accounts', Network],
        ['overview', 'Overview', LayoutDashboard],
        ['leads', 'Leads', Target],
        ['deals', 'Deals', CircleDollarSign],
        ['forecast', 'Forecast', TrendingUp],
        ['contacts', 'Contacts', Contact],
        ['conversations', 'Conversations', Mail],
        ['followups', 'Tasks', ListChecks],
        ['performance', 'Performance', Gauge],
        ['playbook', 'Playbook', BookOpenCheck],
      ] as const).map(([tab, label, icon]) => ({
        to: tab === 'accounts' ? paths.crm(workspaceSlug) : `${paths.crm(workspaceSlug)}?tab=${tab}`,
        label,
        icon,
        // Hidden: the CRM runs on GoHighLevel now, and these older Tapwork CRM pages hold no
        // records (checked 27 Oct 2026). Their pages and data stay; only the menu entries go.
        show: false,
        // Exact path: these are tabs of /crm, and must not light up on /crm/companies and friends.
        end: true,
        matchSearch: (q: URLSearchParams) => (q.get('tab') ?? 'accounts') === tab || (tab === 'accounts' && !CRM_TABS.has(q.get('tab') ?? '')),
      })),
      ],
    },
    {
      // Its own section, out of Workspace: every project, then the live ones by type, each type a
      // folder that opens and closes. Client Projects open their dashboard; internal ones their page.
      label: 'Project Management',
      drawer: { icon: FolderKanban, blurb: 'All projects, Client Projects and internal projects' },
      items: [
        { to: paths.projects(workspaceSlug), label: 'All projects', icon: FolderOpen, show: can('read', 'Project'), end: true },
        // P6: the pilots on the pipeline board, with their projects. Replaces the old Pilots page while the pipeline is on.
        { to: `${paths.pipeline(workspaceSlug)}?view=pilots`, label: 'Pipeline and pilots', icon: Rocket, show: pipelineOn && can('read', 'Project'), matchSearch: (q: URLSearchParams) => q.get('view') === 'pilots' },
        {
          to: paths.projects(workspaceSlug),
          label: 'Client',
          icon: Building2,
          show: can('read', 'Project'),
          children: (projectsNav.data?.client ?? []).map((p) => ({ to: paths.clientProject(workspaceSlug, p.id), label: p.label, icon: Building2, show: true })),
        },
        {
          to: paths.projects(workspaceSlug),
          label: 'Internal',
          icon: FolderKanban,
          show: can('read', 'Project'),
          children: (projectsNav.data?.internal ?? []).map((p) => ({ to: paths.projectPage(workspaceSlug, p.id), label: p.label, icon: FolderKanban, show: true })),
        },
      ],
    },
    {
      label: 'Business',
      drawer: { icon: BriefcaseBusiness, blurb: 'Clients, pilots, finance, procurement and HR' },
      items: [
        { to: paths.clients(workspaceSlug), label: 'Clients', icon: Building2, show: can('read', 'Client') },
        { to: paths.pilots(workspaceSlug), label: 'Pilots', icon: Rocket, show: !pipelineOn && can('read', 'Project') && can('read', 'Card') },
        { to: paths.finance(workspaceSlug), label: 'Finance', icon: WalletCards, show: can('read', 'FinanceRequest') },
        { to: paths.hr(workspaceSlug), label: 'HR', icon: Network, show: true, locked: !can('read', 'Hr') },
        // Listed for everyone so people know it exists; locked for anyone outside the procurement group.
        { to: paths.procurement(workspaceSlug), label: 'Procurement', icon: ShoppingCart, show: true, locked: !can('read', 'Procurement') },
      ],
    },
    {
      // Product's four working lists, behind one drawer entry like the CRM's.
      label: 'Product',
      drawer: { icon: Lightbulb, blurb: 'Documents, UI reviews, the roadmap and decisions' },
      items: ([
        ['docs', 'Documents', FileText],
        ['reviews', 'UI reviews', MessageSquareWarning],
        ['roadmap', 'Roadmap', MapIcon],
        ['decisions', 'Decisions', Gavel],
      ] as const).map(([tab, label, icon]) => ({
        to: tab === 'docs' ? paths.product(workspaceSlug) : `${paths.product(workspaceSlug)}?tab=${tab}`,
        label,
        icon,
        show: can('read', 'Product'),
        matchSearch: (q: URLSearchParams) => (q.get('tab') ?? 'docs') === tab || (tab === 'docs' && !['reviews', 'roadmap', 'decisions'].includes(q.get('tab') ?? '')),
      })),
    },
    {
      // One section: Reports first, a folder with every report and daily reports, then the dashboards.
      label: 'Reports & Analytics',
      items: [
        {
          // Every report is a page of its own, listed here, with an index page above them.
          to: paths.reports(workspaceSlug),
          label: 'Reports',
          icon: ChartNoAxesCombined,
          show: true,
          children: [
            { to: paths.reports(workspaceSlug), label: 'All reports', icon: ChartNoAxesCombined, end: true, show: can('read', 'Card') || can('read', 'Report') },
            { to: paths.reportsSupport(workspaceSlug), label: 'Support', icon: MessagesSquare, show: can('read', 'Card') && !legacyFrozen },
            { to: paths.reporting(workspaceSlug), label: 'Daily reports', icon: ClipboardList, show: can('read', 'Report') },
          ],
        },
        // Gated on Card, not on a subject of its own: the endpoint is `card.read` and counts
        // the very rows the board shows. Anyone who can read the work can read how it went.
        { to: paths.insights(workspaceSlug), label: 'Insights', icon: SquareActivity, show: can('read', 'Card') },
        { to: paths.supportDashboard(workspaceSlug), label: 'Support Dashboard', icon: Headset, show: can('read', 'Card') && !legacyFrozen },
        {
          // The older Support pages, frozen and read-only: kept for history and old links.
          to: paths.supportTicketQueue(workspaceSlug),
          label: 'Archive',
          icon: Archive,
          show: can('read', 'Card') && legacyFrozen,
          children: [
            { to: paths.supportTicketQueue(workspaceSlug), label: 'Old support tickets', icon: LifeBuoy, show: true },
            { to: paths.supportDashboard(workspaceSlug), label: 'Old support dashboard', icon: Headset, show: true },
            { to: paths.reportsSupport(workspaceSlug), label: 'Old support report', icon: MessagesSquare, show: true },
          ],
        },
      ],
    },
    {
      label: 'Organization',
      items: [
        {
          to: paths.teams(workspaceSlug),
          label: 'Teams',
          icon: Users,
          // Defense in depth: client/external sessions belong to the separate portal and
          // must never receive internal organization navigation, even if this shell is ever
          // mounted incorrectly around them.
          show: status === 'signed-in' && can('read', 'Team'),
        },
        { to: paths.people(workspaceSlug), label: 'People', icon: UsersRound, show: can('read', 'Member') },
        {
          to: paths.settingsMembers(workspaceSlug),
          label: 'Settings',
          icon: Settings,
          show: can('read', 'Member') || can('read', 'Role'),
        },
      ],
    },
  ]
  const groups = allGroups
    .map((group) => ({
      ...group,
      items: group.items
        // A folder carries only the children this person may see, and disappears with none.
        .map((item) => (item.children ? { ...item, children: item.children.filter((child) => child.show) } : item))
        .filter((item) => item.show && (!item.children || item.children.length > 0)),
    }))
    .filter((group) => group.items.length > 0)

  /*
   * Presentation mode (a client project shared on screen): the page alone. No navigation, top bar,
   * Dynamic Island, notifications, Office, games, work bar or diary prompt — nothing a client should
   * see or that could pop up mid-call. Dialogs the page opens still work; StageRequestHost stays for
   * the flows that use it.
   */
  if (/\/projects\/[^/]+\/present\/?$/.test(pathname)) {
    return (
      <div className="min-h-dvh min-w-0 bg-background">
        <StageRequestHost />
        <main key={timeZone} className="mx-auto w-full min-w-0 px-3 py-4 md:px-6 md:py-6">
          <Outlet />
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-dvh min-w-0 overflow-x-clip bg-background">
      {/* overflow-x-clip, not -hidden: "hidden" makes this div a scroll container, which pins the
          sticky top bar to it instead of the viewport, so the bar scrolled away with the page. */}
      <ExtensionTokenBridge />
      <aside
        // Above the frame layer, which paints the dark chrome everywhere outside the content card.
        className={cn(
          'group/side scrollbar-none fixed inset-y-0 left-0 z-[calc(var(--tt-z-dock)+2)] hidden h-dvh shrink-0 flex-col bg-nav-bg text-nav-text md:flex',
          // Docked icons grow past the rail over the page, like the Mac Dock over windows.
          sidebar.dock ? 'overflow-visible' : 'overflow-x-hidden overflow-y-auto',
          // Apple's sheet curve: quick to start, long soft settle. Peeking floats over the page.
          'transition-[width,box-shadow] duration-[420ms] ease-[var(--tt-ease-ios)]',
          sidebar.peek && 'shadow-[24px_0_60px_-20px_color-mix(in_srgb,black_55%,transparent)]',
        )}
        style={{ width: sidebar.open ? 'var(--tt-sidebar-w)' : 'var(--tt-sidebar-w-collapsed)' }}
        onMouseEnter={sidebar.pinned ? undefined : sidebar.peekOn}
        onMouseLeave={sidebar.pinned ? undefined : sidebar.peekOff}
        onFocus={sidebar.pinned ? undefined : sidebar.peekOn}
        onBlur={sidebar.pinned ? undefined : (e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) sidebar.peekOff() }}
      >
        <div className={cn('relative flex shrink-0 items-center py-5 transition-[padding] duration-[420ms] ease-[var(--tt-ease-ios)]', sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-5')}>
          <BrandMark />
          <span
            className={cn(
              'overflow-hidden whitespace-nowrap text-xl font-semibold tracking-tight text-white transition-[opacity,max-width,transform] ease-[var(--tt-ease-ios)]',
              sidebarCollapsed ? 'max-w-0 -translate-x-1 opacity-0 duration-150' : 'max-w-[10rem] translate-x-0 opacity-100 delay-75 duration-[420ms]',
            )}
          >
            TapWork
          </span>
          {sidebar.open ? (
            <button
              type="button"
              onClick={sidebar.togglePin}
              aria-label={sidebar.pinned ? 'Unpin sidebar' : 'Pin sidebar'}
              aria-pressed={sidebar.pinned}
              title={`${sidebar.pinned ? 'Unpin — collapse to icons' : 'Pin open'} (${shortcut})`}
              className={cn(
                'ml-auto grid size-8 shrink-0 place-items-center rounded-lg transition-[opacity,background-color,color,transform] duration-200 hover:bg-nav-hover hover:text-white active:scale-90 focus-visible:opacity-100',
                sidebar.pinned ? 'text-nav-icon opacity-0 group-hover/side:opacity-100' : 'bg-white/10 text-white opacity-100',
              )}
            >
              {sidebar.pinned ? <PanelLeftClose className="size-[18px]" /> : <Pin className="size-4" />}
            </button>
          ) : null}
        </div>
        {sidebarCollapsed ? (
          <div className="flex shrink-0 items-center justify-center gap-0.5 pb-3">
            <button
              type="button"
              onClick={() => setSidebarSettingsOpen(true)}
              aria-label="Sidebar settings"
              title="Sidebar settings"
              className="grid size-8 place-items-center rounded-lg text-nav-icon transition-[background-color,color,transform] hover:bg-nav-hover hover:text-white active:scale-90"
            >
              <Settings2 className="size-[18px]" />
            </button>
            <button
              type="button"
              onClick={sidebar.togglePin}
              aria-label="Pin sidebar open"
              title={`Pin sidebar open (${shortcut})`}
              className="grid size-8 place-items-center rounded-lg text-nav-icon transition-[background-color,color,transform] hover:bg-nav-hover hover:text-white active:scale-90"
            >
              <PanelLeftOpen className="size-[18px]" />
            </button>
          </div>
        ) : null}
        <div className="border-t border-nav-line" />
        {sidebar.dock ? (
          // The Dock has no room to nest: a folder's children take its place.
          <SidebarDock groups={flattenNavGroups(groups)} magnification={sidebarMagnification} />
        ) : (
          <SidebarNav groups={groups} collapsed={sidebarCollapsed} className="flex-1" />
        )}
        {sidebar.dock ? <div className="flex-1" /> : null}
      </aside>

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="flex w-[min(86vw,20rem)] flex-col gap-0 border-0 bg-nav-bg p-0 text-nav-text md:hidden">
          <SheetHeader className="px-5 py-5 text-left">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <div className="flex items-center gap-3 pr-8">
              <BrandMark />
              <span className="text-xl font-semibold tracking-tight text-white">TapWork</span>
            </div>
          </SheetHeader>
          <div className="border-t border-nav-line" />
          <SidebarNav groups={groups} onNavigate={() => setMobileNavOpen(false)} className="scrollbar-none flex-1 overflow-y-auto" />
        </SheetContent>
      </Sheet>

      <div aria-hidden className="tt-frame" />
      <div className="flex min-w-0 flex-1 flex-col transition-[margin] duration-[420ms] ease-[var(--tt-ease-ios)] md:ml-[var(--tt-sidebar-current)] md:py-[var(--tt-frame-gap)] md:pr-[var(--tt-frame-gap)]">
        <header
          className="sticky top-0 z-dock md:top-[var(--tt-frame-gap)] flex shrink-0 items-center gap-2 border-b border-border bg-surface/85 px-2 backdrop-blur-sm md:gap-4 md:px-4"
          style={{ height: 'var(--tt-topbar-h)' }}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-9 shrink-0 md:hidden"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="size-5" />
          </Button>
          {/* Search: compact, on the left. The free middle is the Dynamic Island's home. */}
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:w-[min(18rem,26vw)] sm:flex-none">
            <WorkspaceSwitcher slug={workspaceSlug} />
            <GlobalCommandPalette workspaceSlug={workspaceSlug} workspaceId={workspace?.id} />
          </div>
          <div data-island-slot aria-hidden className="hidden min-w-0 flex-1 self-stretch md:block" />

          <div className="flex shrink-0 items-center gap-1">
          <Button variant="ghost" size="sm" className="hidden h-9 gap-1.5 md:inline-flex" asChild>
            <a
              href="/downloads/tapwork-chrome-extension.zip"
              download="tapwork-chrome-extension.zip"
              title={
                extension.installed
                  ? `Tapwork extension: installed v${extension.installed}, latest v${extension.latest ?? extension.installed}`
                  : extension.legacy
                    ? 'An older Tapwork extension is installed. Download the latest to update.'
                    : `Not installed. Download the latest Tapwork Chrome extension${extension.latest ? ` (v${extension.latest})` : ''}`
              }
            >
              <Download className="size-4" />
              <span className="hidden lg:inline">
                {extension.installed
                  ? extension.latest && !versionAtLeast(extension.installed, extension.latest)
                    ? `Update: you v${extension.installed} · latest v${extension.latest}`
                    : `Extension v${extension.installed} · up to date`
                  : extension.legacy
                    ? `Update: you are on an old version · latest v${extension.latest ?? ''}`
                    : `Install Extension${extension.latest ? ` v${extension.latest}` : ''}`}
              </span>
            </a>
          </Button>
          <Button variant="ghost" size="sm" className="h-9 gap-1.5" asChild>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSdyIiW6CUCMcGafhJGT37Dpr4cXlVed4E2IIp3z6xZz21Fhng/viewform?usp=dialog"
              target="_blank"
              rel="noreferrer"
            >
              <MessageSquarePlus className="size-4" /><span className="hidden md:inline">Feedback Form</span>
            </a>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFocusOpen(true)}
            aria-label={focusOn ? `Focus on, ${untilLabel(focus.until)}` : 'Focus mode'}
            title={focusOn ? `Focus on · ${untilLabel(focus.until)}` : focus.supportL3.enabled ? 'Focus mode · L3 watch on' : 'Focus mode'}
            className={cn('relative h-9 gap-1.5 px-2.5', focusOn && 'bg-brand text-text-inverse hover:bg-brand/90 hover:text-text-inverse')}
          >
            <Moon className="size-4" fill={focusOn ? 'currentColor' : 'none'} />
            {focusOn ? <span className="hidden text-xs font-semibold sm:inline">Focus</span> : null}
            {focus.supportL3.enabled ? <span className="absolute right-1 top-1 size-2 rounded-full bg-ios-green ring-2 ring-surface" aria-hidden /> : null}
          </Button>
          {getViewAs() === null ? <MusicButton /> : null}
          <ActiveWorkChip workspaceSlug={workspaceSlug} enabled={getViewAs() === null} />
          {/* Beside the account menu, which is where people look for "what happened". */}
          <NotificationBell workspaceSlug={workspaceSlug} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 gap-2 pr-2 pl-1.5">
                <UserAvatar person={meAsPerson} size="sm" />
                <span className="hidden max-w-48 truncate text-xs text-text-secondary md:inline">
                  {meAsPerson.displayName}
                </span>
                <ChevronDown className="size-3.5 text-text-muted" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="flex items-center gap-2 font-normal">
                <UserAvatar person={meAsPerson} size="md" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">
                    {meAsPerson.displayName}
                  </span>
                  <span className="block truncate text-tiny text-text-muted">{email}</span>
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/*
                `onSelect` is prevented so the menu does not close under the dialog it just
                opened — a Radix menu item dismisses the whole menu by default, and the
                dialog would be unmounted with it.
              */}
              <DropdownMenuItem onSelect={() => setPhotoOpen(true)}>
                <ImagePlus className="size-4" />
                Profile photo
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setTimeZoneOpen(true)} className="items-start">
                <Globe2 className="mt-0.5 size-4" />
                <span className="min-w-0">
                  <span className="block">Timezone</span>
                  <span className="block truncate text-tiny text-text-muted">{shortTimeZoneLabel(timeZone)}</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setSidebarSettingsOpen(true)} className="items-start">
                <PanelLeft className="mt-0.5 size-4" />
                <span className="min-w-0">
                  <span className="block">Sidebar</span>
                  <span className="block text-tiny text-text-muted">{sidebar.pinned ? 'Pinned open' : sidebarBehavior === 'dock' ? 'Folded · Dock' : 'Folded · slides open'}</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setFocusOpen(true)} className="items-start">
                <Moon className="mt-0.5 size-4" />
                <span className="min-w-0">
                  <span className="block">Focus mode</span>
                  <span className="block text-tiny text-text-muted">{focusOn ? `On · ${untilLabel(focus.until)}` : focus.supportL3.enabled ? 'Off · L3 watch on' : 'Choose what interrupts you'}</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setIslandSettingsOpen(true)} className="items-start">
                <Sparkles className="mt-0.5 size-4" />
                <span className="min-w-0">
                  <span className="block">Dynamic Island</span>
                  <span className="block text-tiny text-text-muted">Time, weather, reminders, alerts</span>
                </span>
              </DropdownMenuItem>
              {has('member.viewAs') && !viewingAs ? (
                <DropdownMenuItem onSelect={() => setViewAsOpen(true)} className="items-start">
                  <Eye className="mt-0.5 size-4" />
                  <span className="min-w-0">
                    <span className="block">View as…</span>
                    <span className="block text-tiny text-text-muted">See Tapwork as a colleague, read-only</span>
                  </span>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onSelect={() => setCalendarOpen(true)} className="items-start">
                <CalendarDays className="mt-0.5 size-4" />
                <span className="min-w-0">
                  <span className="block">Google Calendar</span>
                  <span className="block text-tiny text-text-muted">Link your calendar</span>
                </span>
              </DropdownMenuItem>
              {app.canInstall && !app.installed ? (
                <DropdownMenuItem
                  onSelect={() => {
                    void app.install().then((ok) => { if (ok) toast.success('TapWork installed. Open it from your dock or apps.') })
                  }}
                  className="items-start"
                >
                  <MonitorDown className="mt-0.5 size-4" />
                  <span className="min-w-0">
                    <span className="block">Install TapWork app</span>
                    <span className="block text-tiny text-text-muted">Its own window, in your dock</span>
                  </span>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onSelect={() => void signOut()}>
                <LogOut className="size-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

      {/* A sibling of the menu, never a child of it. */}
      <ProfilePhotoDialog open={photoOpen} onOpenChange={setPhotoOpen} />
      {timeZoneOpen ? <TimeZoneDialog open onOpenChange={setTimeZoneOpen} /> : null}
      {islandSettingsOpen ? <IslandSettingsDialog open onOpenChange={setIslandSettingsOpen} /> : null}
      {calendarOpen ? <GoogleCalendarDialog open onOpenChange={setCalendarOpen} /> : null}
      {viewAsOpen ? <ViewAsDialog open onOpenChange={setViewAsOpen} /> : null}
      <ViewAsBanner />
      {focusOpen ? <FocusModeDialog open onOpenChange={setFocusOpen} /> : null}
      <StageRequestHost />
      <StagePromptHost />
      {sidebarSettingsOpen ? (
        <SidebarSettingsDialog open onOpenChange={setSidebarSettingsOpen} pinned={sidebar.pinned} onPinnedChange={sidebar.setPinned} />
      ) : null}
      <DynamicIsland workspaceSlug={workspaceSlug} onOpenSettings={() => setIslandSettingsOpen(true)} />
      <DiaryGate enabled={!viewingAs} />
      <OfficeWidget workspaceSlug={workspaceSlug} />
      <GamesHost />
      <StickyWorkBar workspaceSlug={workspaceSlug} enabled={getViewAs() === null && !pathname.endsWith('/office')} />

        <ExtensionPrompt />
        <main
          // Keyed by the zone so every time on the page re-renders when the person changes it.
          key={timeZone}
          className="mx-auto flex min-h-0 w-full min-w-0 flex-1 flex-col px-3 py-4 md:px-5 md:py-5"
          style={{ maxWidth: 'var(--tt-page-max)', paddingBottom: 'calc(var(--tt-float-h, 0px) + 1.25rem)' }}
        >
          {/* Keyed by path so each page plays its entrance; query-string changes (filters, an
              opened card) do not replay it. */}
          <div key={pathname} className="tt-page-enter flex min-h-0 min-w-0 flex-1 flex-col">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

function WorkspaceSwitcher({ slug }: { slug: string }) {
  const { workspace, workspaces } = useWorkspace()
  const navigate = useNavigate()
  const name = workspace?.name ?? slug ?? 'Workspace'

  const monogram = (
    <span
      aria-hidden
      className="grid size-6 shrink-0 place-items-center rounded-sm bg-gradient-to-br from-brand to-brand-accent text-micro font-bold text-text-inverse"
    >
      {name.slice(0, 1).toUpperCase()}
    </span>
  )

  // With one workspace there is nothing to switch to, and a label of it is just noise in the
  // top bar. The switcher only appears once there is a second workspace.
  if (workspaces.length <= 1) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 gap-2 pr-2 pl-1.5 font-medium max-md:min-w-0">
          {monogram}
          <span className="max-md:max-w-[5rem] max-md:truncate">{name}</span>
          <ChevronDown className="size-3.5 text-text-muted" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {workspaces.map((w) => (
          <DropdownMenuItem key={w.id} onSelect={() => navigate(paths.work(w.slug))}>
            {w.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
